import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import { config } from "../config";
import { useEventsApi } from "../hooks/useEventsApi";
import CustomAlert from "./CustomAlert";
import getUserRoleFlags from "../hooks/useGetUserRoleFlags";
import type { CounterpartEventRef } from "../utils/counterpartEvent";
import { resolveCounterpartGlobalIds } from "../utils/counterpartEvent";
import { isResolvingBandCode } from "../shared/eventCodeRange";

interface Props {
  event: CounterpartEventRef;
  setEditEventId: (id: number) => void;
  onClose?: () => void;
}

const EventMenuButton: React.FC<Props> = ({ event, setEditEventId, onClose }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { isAdmin } = getUserRoleFlags();

  const handleOpenMenu = (ev: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(ev.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const handleShowDetails = () => {
    if (event.global_event_id) setEditEventId(event.global_event_id);
    handleCloseMenu();
  };

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"error" | "success">("error");
  const { callApi } = useEventsApi();

  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [counterparts, setCounterparts] = useState<{
    rising: number | null;
    resolving: number | null;
  } | null>(null);
  const [counterLoading, setCounterLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setCounterparts(null);
      return;
    }
    let cancelled = false;
    setCounterLoading(true);
    void resolveCounterpartGlobalIds(callApi, event).then((cp) => {
      if (!cancelled) {
        setCounterparts(cp);
        setCounterLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    callApi,
    event.global_event_id,
    event.event_code,
    event.event_type,
    event.event_contract_name,
    event.attributes?.creator_resolving_linked_rising_global_id,
  ]);

  const showRisingJump =
    isResolvingBandCode(Number(event.event_code)) && counterparts?.rising != null;
  const showResolvingJump =
    !isResolvingBandCode(Number(event.event_code)) && counterparts?.resolving != null;

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await callApi(`${config.apiUrl}/api/events/${id}/`, "DELETE");
      setAlertMessage(`Succesfully deleted event with ID ${id}!`);
      setAlertSeverity("error");
      setAlertOpen(true);
    } catch (error) {
      console.error("Error while deleting:", error);
    } finally {
      setLoading(false);
      handleCloseMenu();
    }
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpenMenu}>
        <MoreHoriz />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handleShowDetails}>Show details</MenuItem>
        {counterLoading && (
          <MenuItem disabled sx={{ gap: 1 }}>
            <CircularProgress size={18} />
            Looking up counterpart…
          </MenuItem>
        )}
        {!counterLoading && showRisingJump && (
          <MenuItem
            onClick={() => {
              if (counterparts?.rising != null) {
                setEditEventId(counterparts.rising);
                handleCloseMenu();
              }
            }}
          >
            Open rising event
          </MenuItem>
        )}
        {!counterLoading && showResolvingJump && (
          <MenuItem
            onClick={() => {
              if (counterparts?.resolving != null) {
                setEditEventId(counterparts.resolving);
                handleCloseMenu();
              }
            }}
          >
            Open resolving event
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem onClick={() => setConfirmOpen(true)} sx={{ color: "error.main" }}>
            Delete
          </MenuItem>
        )}
      </Menu>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this event? This action cannot be undone. Note: only the highest
          version will be removed!
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              handleCloseMenu();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (event.global_event_id) handleDelete(event.global_event_id);
              setConfirmOpen(false);
            }}
            color="error"
            disabled={loading || !isAdmin}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Delete highest version
          </Button>
        </DialogActions>
      </Dialog>
      <CustomAlert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        message={alertMessage}
        severity={alertSeverity}
      />
    </>
  );
};

export default EventMenuButton;
